import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isAfter, parseISO } from 'date-fns';

import api from '~/services/api';
import { useStoreActions, useStoreState } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error';
import Modal from '~/ui/components/common/Modal';
import Button from '~/ui/components/common/Button';
import { isoToFormat } from '~/utils/date';

import styles from './PauseMonitoringCancelModal.module.scss';

interface IProps {
  onClose: () => void;
}

const PauseMonitoringCancelModal = ({ onClose }: IProps): ReactElement => {
  const [isProcessing, setIsProcessing] = useState(false);
  const isAutoResumeStarted = useRef(false);

  const patientInfo = useStoreState(state => state.patient.info);
  const currentMonitoringPause = patientInfo?.currentMonitoringPause;
  const patientId = patientInfo?.id;

  const { onGetInfo } = useStoreActions(actions => actions.patient);
  const { showError, showNotify } = useStoreActions(actions => actions.snackbar);

  const shouldConfirmResume = useMemo(
    () =>
      currentMonitoringPause
        ? isAfter(parseISO(currentMonitoringPause.endDate), new Date())
        : false,
    [currentMonitoringPause?.endDate],
  );

  const handleResumeMonitoring = useCallback(async () => {
    if (!patientId) return;

    try {
      setIsProcessing(true);

      await api.patientMonitoringPauses.cancel(currentMonitoringPause.id);
      await onGetInfo(patientId);

      showNotify('Monitoring resumed');
      onClose();
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsProcessing(false);
    }
  }, [onClose, onGetInfo, patientId, showError, showNotify]);

  useEffect(() => {
    if (!currentMonitoringPause) {
      onClose();
      return;
    }

    if (!shouldConfirmResume && !isAutoResumeStarted.current) {
      isAutoResumeStarted.current = true;
      handleResumeMonitoring();
    }
  }, [currentMonitoringPause, handleResumeMonitoring, onClose, shouldConfirmResume]);

  const handleClose = () => {
    if (isProcessing) return;

    onClose();
  };

  if (!currentMonitoringPause) return null;

  return (
    <Modal open title="Resume monitoring?" className={styles.modal} onClose={handleClose}>
      <div className={styles.inner}>
        <div className={styles.description}>
          {shouldConfirmResume ? (
            <>
              This patient is currently paused until{' '}
              <strong>
                {isoToFormat(
                  currentMonitoringPause.endDate,
                  'dd MMM yyyy',
                  patientInfo?.timezone,
                  true,
                )}
              </strong>
              . Are you sure you want to resume monitoring before the scheduled end date?
            </>
          ) : (
            <>Monitoring pause has reached the end date. Resuming monitoring now...</>
          )}
        </div>
        <div className={styles.btnWrapper}>
          <Button variant="outlined" color="default" disabled={isProcessing} onClick={handleClose}>
            {shouldConfirmResume ? 'Cancel' : 'Close'}
          </Button>
          <div className={styles.widthSpacer} />
          <Button
            variant="contained"
            color="primary"
            isLoading={isProcessing}
            disabled={isProcessing}
            onClick={handleResumeMonitoring}
          >
            Resume Monitoring
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default PauseMonitoringCancelModal;
